import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  HeartPulse, 
  Activity, 
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Expand,
  Phone
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import GlassCard from '../components/GlassCard';
import GlassButton from '../components/GlassButton';
import SEO from '../components/SEO';

gsap.registerPlugin(ScrollTrigger);

const SurgeriesPage = () => {

  const containerRef = useRef(null);
  const galleryRef = useRef(null);
  const [activeGallery, setActiveGallery] = useState(null);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const surgeries = [
    {
      title: "Orthopedic Surgery",
      subtitle: "Bone & Joint Excellence",
      desc: "Specialized procedures for bone fractures and joint injuries in dogs and cats. Best orthopedic pet surgeon in Hyderabad.",
      image: "/images/WhatsApp Image 2026-01-23 at 11.59.22 PM.jpeg",
      gallery: [
        "/images/WhatsApp Image 2026-01-23 at 11.59.22 PM.jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.59.22 PM (1).jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.59.22 PM (2).jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.58.30 PM.jpeg"
      ],
      stat: "Success",
      statVal: "98%"
    },
    {
      title: "Tumor & Mass Removal",
      subtitle: "Safety First Protocol",
      desc: "Safe and precise tumor removal surgeries for pets with a high success rate and compassionate post-op care.",
      image: "/images/WhatsApp Image 2026-01-23 at 11.58.30 PM.jpeg",
      gallery: [
        "/images/WhatsApp Image 2026-01-23 at 11.58.30 PM.jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.58.30 PM (1).jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.59.20 PM.jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.59.20 PM (2).jpeg"
      ],
      stat: "Safety",
      statVal: "Gold"
    },
    {
      title: "Advanced Diagnostics",
      subtitle: "Internal Medicine",
      desc: "State-of-the-art X-ray and imaging facilities to provide precise pre-operative assessments and diagnosis.",
      image: "/images/WhatsApp Image 2026-01-23 at 11.59.21 PM.jpeg",
      gallery: [
        "/images/WhatsApp Image 2026-01-23 at 11.59.21 PM.jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.59.21 PM (1).jpeg",
        "/images/WhatsApp Image 2026-01-23 at 11.59.21 PM (2).jpeg",
        "/images/ultra-sound.jpeg"
      ],
      stat: "Precision",
      statVal: "Elite"
    },
    {
      title: "Post-Op Recovery",
      subtitle: "Compassionate Care",
      desc: "Dedicated monitoring and pain management to ensure your pet's comfort and quick return to health.",
      image: "/images/WhatsApp Image 2026-01-24 at 12.00.40 AM.jpeg",
      gallery: [
        "/images/WhatsApp Image 2026-01-24 at 12.00.40 AM.jpeg",
        "/images/WhatsApp Image 2026-01-24 at 12.00.40 AM (1).jpeg",
        "/images/WhatsApp Image 2026-01-24 at 12.00.39 AM.jpeg",
        "/images/WhatsApp Image 2026-01-24 at 12.00.39 AM (1).jpeg"
      ],
      stat: "Care",
      statVal: "24/7"
    }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Header Animation
      gsap.fromTo('.reveal-up', 
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, stagger: 0.1, ease: "expo.out" }
      );

      let mm = gsap.matchMedia();
      
      // Horizontal Scroll Gallery (Premium Interaction)
      mm.add("(min-width: 1025px)", () => {
        let sections = gsap.utils.toArray(".surgery-card-horizontal");
        gsap.to(sections, {
          xPercent: -100 * (sections.length - 1),
          ease: "none",
          scrollTrigger: {
            trigger: galleryRef.current,
            pin: true,
            scrub: 1,
            snap: 1 / (sections.length - 1),
            end: () => "+=" + galleryRef.current.offsetWidth
          }
        });
      });

      // Card Stagger (Mobile)
      mm.add("(max-width: 1024px)", () => {
          gsap.fromTo('.surgery-card-horizontal', 
            { y: 40, opacity: 0 },
            { 
                y: 0, 
                opacity: 1, 
                duration: 0.8, 
                stagger: 0.2, 
                scrollTrigger: {
                    trigger: '.surgery-card-horizontal',
                    start: "top 80%"
                }
            }
          );
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const openLightbox = (index, surgery) => {
    setActiveGallery(surgery.gallery);
    setCurrentImgIndex(0);
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-background overflow-clip pt-16"
    >
      <SEO 
        title="Pet Surgery Center in Vanasthalipuram, Hyderabad"
        description="Expert pet surgical procedures in Vanasthalipuram. We specialize in orthopedic surgery, tumor removal, and emergency pet operations with 24/7 support."
        canonical="/surgeries"
      />

      {/* Lightbox Modal */}
      <AnimatePresence>
        {activeGallery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/95 backdrop-blur-2xl p-4 md:p-20 overflow-hidden"
          >
            <button 
              onClick={() => setActiveGallery(null)}
              className="absolute top-8 right-8 text-white/40 hover:text-white transition-colors"
            >
              <Expand size={32} className="rotate-45" />
            </button>

            <div className="relative w-full h-full flex items-center justify-center">
               <motion.img
                 key={currentImgIndex}
                 initial={{ opacity: 0, scale: 0.9 }}
                 animate={{ opacity: 1, scale: 1 }}
                 src={activeGallery[currentImgIndex]}
                 alt="Gallery Detail"
                 className="max-w-full max-h-full object-contain rounded-[5px] shadow-2xl"
               />
               
               <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4">
                 {activeGallery.map((_, i) => (
                   <button
                    key={i}
                    onClick={() => setCurrentImgIndex(i)}
                    className={`w-12 h-1 bg-white transition-all rounded-full ${i === currentImgIndex ? 'opacity-100' : 'opacity-20'}`}
                   />
                 ))}
               </div>

               <button 
                onClick={() => setCurrentImgIndex((prev) => (prev - 1 + activeGallery.length) % activeGallery.length)}
                className="absolute left-0 w-20 h-full flex items-center justify-center text-white/20 hover:text-white transition-all"
               >
                 <ChevronLeft size={48} />
               </button>

               <button 
                onClick={() => setCurrentImgIndex((prev) => (prev + 1) % activeGallery.length)}
                className="absolute right-0 w-20 h-full flex items-center justify-center text-white/20 hover:text-white transition-all"
               >
                 <ChevronRight size={48} />
               </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Header */}
      <section className="relative pt-[140px] md:pt-[140px] pb-16 overflow-hidden">
        {/* Cinematic Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Text Content */}
            <div className="space-y-8">
              <div className="reveal-up inline-flex items-center gap-2 px-4 py-2 bg-primary/5 backdrop-blur-xl rounded-full border border-ios-blue/10">
                <Sparkles size={14} className="text-primary" />
                <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-primary">Surgical Excellence</span>
              </div>
              <h1 className="reveal-up text-navy font-display font-bold leading-[0.95] tracking-tighter text-4xl md:text-5xl lg:text-7xl">
                Surgical Services & <br />
                <span className="italic font-normal text-primary italic-ios">Expert Pet Operations.</span>
              </h1>
              <p className="reveal-up text-luxury text-xl md:text-2xl leading-snug max-w-2xl font-medium">
                We provide a wide range of pet surgical procedures using modern equipment and safety protocols in a clean and sterile environment.
              </p>
            </div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative group hidden lg:block"
            >
              <div className="relative rounded-[4.5rem] overflow-hidden shadow-glass-heavy border border-white/20 bg-white">
                <img 
                  src="/images/services/surgical_theater.png" 
                  alt="Dr. Suresh Pet Care Surgical Theatre" 
                  className="w-100 h-auto max-h-[600px] object-cover object-center transition-transform duration-[10s] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-navy/30 to-transparent pointer-events-none" />
              </div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-primary/10 rounded-full blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Showcase Gallery */}
      <section ref={galleryRef} className="lg:h-screen flex items-center bg-transparent relative overflow-hidden">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-0 lg:absolute lg:top-0 lg:left-0 lg:h-full lg:px-[10vw] items-center py-12 lg:py-0">
          {surgeries.map((item, i) => (
            <div key={i} className="surgery-card-horizontal w-full lg:w-[45vw] lg:h-[85vh] flex-shrink-0 px-4 lg:px-12 group cursor-pointer" onClick={() => openLightbox(i, item)}>
              <div className="relative h-full bg-white border border-border shadow-sm rounded-[4rem] lg:rounded-[5rem] !p-10 border-white/40 shadow-glass-heavy overflow-hidden transition-all duration-700 group-hover:bg-white/50">
                <div className="relative z-20 space-y-4 mb-10">
                   <div className="flex flex-wrap items-center gap-3">
                      <span className="px-4 py-1.5 bg-primary/10 backdrop-blur-md rounded-full text-[10px] font-bold text-primary uppercase tracking-widest border border-ios-blue/10">
                         {item.stat}: {item.statVal}
                      </span>
                      <span className="flex items-center gap-2 text-[10px] font-bold text-primary/60 uppercase tracking-widest">
                         <Expand size={14} /> View High-Res Gallery
                      </span>
                   </div>
                   <h3 className="text-4xl md:text-5xl font-bold tracking-tighter text-navy group-hover:text-primary transition-colors leading-tight">{item.title}</h3>
                   <p className="text-luxury text-sm md:text-base max-w-sm font-medium opacity-60 leading-relaxed">{item.subtitle}</p>
                </div>
 
                <div className="relative aspect-auto lg:h-[calc(100%-180px)] rounded-[3rem] overflow-hidden shadow-2xl bg-transparent">
                   <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-full h-full object-cover object-center transition-all duration-1000 group-hover:scale-105"
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80 pointer-events-none" />
                   
                   <div className="absolute bottom-8 left-8 right-8 p-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2.5rem] opacity-0 translate-y-6 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-700">
                      <p className="text-sm md:text-base text-white leading-relaxed font-medium italic pr-4">
                        {item.desc}
                      </p>
                   </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Post-Op Info */}
      <section>
        <div className="section-container grid md:grid-cols-3 gap-8">
           {[
             { icon: <HeartPulse className="text-primary" />, title: "Pre-Surgery", desc: "Patient health evaluation before any procedure to ensure safety and readiness." },
             { icon: <ShieldCheck className="text-ios-green" />, title: "Live Monitoring", desc: "Careful monitoring during surgery using advanced equipment and safety protocols." },
             { icon: <Activity className="text-ios-gold" />, title: "Recovery Support", desc: "Comprehensive post-surgery recovery support to ensure a healthy return to home." }
           ].map((info, i) => (
             <GlassCard key={i} className="!p-10 !rounded-[2.5rem] border-navy/5 bg-white/50 backdrop-blur-md space-y-6 shadow-glass-light hover:shadow-glass-heavy transition-all duration-700 hover:-translate-y-2">
                <div className="w-16 h-16 rounded-[1.2rem] bg-navy text-white flex items-center justify-center shadow-lg shadow-navy/20">
                   {info.icon}
                </div>
                <h4 className="text-2xl font-bold text-navy">{info.title}</h4>
                <p className="text-muted-foreground text-sm font-medium leading-relaxed">{info.desc}</p>
             </GlassCard>
           ))}
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="pb-24">
        <div className="section-container">
           <GlassCard className="!bg-[#020b1c] !border-white/5 !p-16 md:!p-24 !rounded-[4.5rem] text-white flex flex-col lg:flex-row items-center justify-between gap-12 text-center lg:text-left shadow-glass-heavy relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-[40%] h-full bg-primary/5 blur-[120px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-[30%] h-full bg-ios-blue/5 blur-[100px] pointer-events-none" />
              
              <div className="space-y-8 relative z-10">
                 <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
                    <Activity size={14} className="text-primary animate-pulse" />
                    <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/60">Trauma Response Unit</span>
                 </div>
                 <h3 className="text-5xl md:text-7xl text-white tracking-tighter leading-[0.95] font-bold">
                    Emergency Surgery <br />
                    <span className="italic font-normal text-primary italic-ios">Available 24/7.</span>
                 </h3>
                 <p className="text-white/40 text-xl max-w-md font-medium leading-relaxed">Our clinical trauma team is ready for immediate life-saving surgical intervention.</p>
              </div>

              <a href={`https://wa.me/919848954921?text=${encodeURIComponent("Hi Dr. Suresh, I have a surgical emergency and need immediate assistance for my pet.")}`} 
                 target="_blank" rel="noopener noreferrer"
                 className="relative z-10 btn-animate">
                <GlassButton className="!bg-white !text-navy !px-16 !h-20 text-sm font-bold tracking-widest !rounded-full shadow-2xl hover:scale-105 transition-all">
                   <Phone size={18} className="mr-3" /> CALL NOW
                </GlassButton>
              </a>
           </GlassCard>
        </div>
      </section>
    </motion.div>
  );
};

export default SurgeriesPage;
